import { useEffect, useState } from "react";
import { Sparkles, Edit3 } from "lucide-react";

const STEPS = [
  {
    label: "Caption",
    input: "Casual Instagram caption for my morning coffee ritual",
    tag: "📸 Instagram",
    output: "6am. coffee. silence before the chaos. this is my entire personality and I'm at peace with that ☕",
    tone: "casual",
    edit: null,
  },
  {
    label: "Cold DM",
    input: "Cold LinkedIn DM to a founder whose product I love",
    tag: "📩 Cold DM",
    output: null,
    tone: null,
    edit: "Cold LinkedIn DM to a founder whose product I love",
  },
  {
    label: "Result",
    input: null,
    tag: "🚀 Product Launch",
    output: "Hey [Name] — huge fan of what you've built. I've been using [product] for 3 months and it genuinely changed how I work. Not pitching anything, just wanted to say it. Would love to grab 15 min if you're ever open to chatting about how early users actually use it. No agenda. 🙏",
    tone: "you",
    edit: null,
  },
] as const;

const TYPING_SPEED = 18;

export default function GetCreativeSection() {
  const [step, setStep] = useState(0);
  const [displayedText, setDisplayedText] = useState("");
  const [typingDone, setTypingDone] = useState(false);

  useEffect(() => {
    setDisplayedText("");
    setTypingDone(false);

    const current = STEPS[step];
    const target = current.output ?? current.edit ?? "";
    if (!target) {
      setTypingDone(true);
      return;
    }

    let i = 0;
    const iv = setInterval(() => {
      i++;
      setDisplayedText(target.slice(0, i));
      if (i >= target.length) {
        clearInterval(iv);
        setTypingDone(true);
      }
    }, TYPING_SPEED);

    return () => clearInterval(iv);
  }, [step]);

  useEffect(() => {
    if (!typingDone) return;
    const delay = step === 1 ? 1200 : 3200;
    const t = setTimeout(() => {
      setStep((s) => (s + 1) % STEPS.length);
    }, delay);
    return () => clearTimeout(t);
  }, [typingDone, step]);

  const current = STEPS[step];
  const isEdit = step === 1;
  const isFinal = step === 2;

  return (
    <section className="py-20 px-4 sm:px-6 relative overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 40% at 30% 60%, hsl(265 75% 62% / 0.06) 0%, transparent 60%)",
        }}
      />
      <div className="max-w-5xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Copy side */}
          <div>
            <p className="text-xs text-cyan-400 font-semibold uppercase tracking-widest mb-3" style={{ filter: "drop-shadow(0 0 6px hsl(188 90% 42% / 0.55))" }}>
              Get Creative
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold mb-5 leading-tight">
              Don't just sound smart.{" "}
              <span className="bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent">
                Sound like YOU.
              </span>
            </h2>
            <p className="text-slate-300 leading-relaxed mb-6">
              ScribPost gives you a starting point, but you're always in control. Go formal, go goofy, go full chaos — edit it into anything you want.
            </p>
            <div className="space-y-3">
              {[
                { icon: "✍️", label: "Start from a rough idea or voice note" },
                { icon: "🎭", label: "Make it serious, funny, or unhinged" },
                { icon: "🔄", label: "Tweak it until it's unmistakably you" },
              ].map(({ icon, label }) => (
                <div key={label} className="flex items-center gap-3">
                  <span className="text-lg">{icon}</span>
                  <span className="text-sm text-slate-300">{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Animated demo side */}
          <div className="rounded-2xl border border-white/10 glass-panel overflow-hidden"
            style={{ background: "hsl(222 44% 6% / 0.95)", boxShadow: "0 0 50px hsl(265 75% 62% / 0.07)" }}>
            {/* Terminal bar */}
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/8 bg-white/[0.02]">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
                <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
              </div>
              <div className="flex items-center gap-1.5 ml-2">
                <Sparkles className="w-3 h-3 text-cyan-400" />
                <span className="text-[11px] text-slate-400">ScribPost Studio</span>
              </div>
              <div className="ml-auto">
                <span
                  className="text-[10px] font-semibold px-2 py-0.5 rounded-full border transition-all duration-500"
                  style={{
                    background: isFinal ? "hsl(265 75% 62% / 0.15)" : "hsl(188 90% 42% / 0.12)",
                    borderColor: isFinal ? "hsl(265 75% 62% / 0.4)" : "hsl(188 90% 42% / 0.3)",
                    color: isFinal ? "hsl(265 75% 80%)" : "hsl(188 90% 70%)",
                  }}
                >
                  {current.tag}
                </span>
              </div>
            </div>

            <div className="p-5 space-y-4">
              {/* Prompt input area */}
              {(step === 0 || step === 1) && (
                <div className="rounded-xl border border-white/8 bg-white/[0.03] p-3">
                  <p className="text-[11px] text-slate-500 mb-1.5 font-medium uppercase tracking-wide">Prompt</p>
                  <p className="text-[13px] text-slate-200">
                    {step === 0 ? STEPS[0].input : null}
                    {step === 1 && (
                      <span className="text-violet-300">
                        {displayedText}
                        <span className="inline-block w-0.5 h-3.5 bg-violet-400 ml-0.5 animate-pulse align-middle" />
                      </span>
                    )}
                  </p>
                </div>
              )}

              {/* Output area */}
              <div
                className="rounded-xl border p-4 transition-all duration-500"
                style={{
                  borderColor: isFinal ? "hsl(265 75% 62% / 0.3)" : "hsl(188 90% 42% / 0.2)",
                  background: isFinal ? "hsl(265 75% 62% / 0.06)" : "hsl(188 90% 42% / 0.05)",
                }}
              >
                {!isEdit && (
                  <>
                    <div className="flex items-center gap-1.5 mb-2.5">
                      {isFinal ? (
                        <Sparkles className="w-3 h-3 text-violet-400" />
                      ) : (
                        <Edit3 className="w-3 h-3 text-cyan-400" />
                      )}
                      <span className="text-[10px] font-semibold uppercase tracking-widest"
                        style={{ color: isFinal ? "hsl(265 75% 70%)" : "hsl(188 90% 70%)" }}>
                        {isFinal ? "In Your Voice" : "First Draft"}
                      </span>
                    </div>
                    <p className="text-[13px] leading-relaxed whitespace-pre-line"
                      style={{ color: isFinal ? "hsl(215 20% 92%)" : "hsl(215 20% 75%)" }}>
                      {step === 0 ? (
                        <>
                          {displayedText}
                          {!typingDone && <span className="inline-block w-0.5 h-3.5 bg-cyan-400 ml-0.5 animate-pulse align-middle" />}
                        </>
                      ) : (
                        <>
                          {displayedText}
                          {!typingDone && <span className="inline-block w-0.5 h-3.5 bg-violet-400 ml-0.5 animate-pulse align-middle" />}
                        </>
                      )}
                    </p>
                  </>
                )}

                {isEdit && (
                  <div className="text-center py-4">
                    <div className="inline-flex items-center gap-2 text-sm text-slate-400">
                      <Sparkles className="w-4 h-4 text-violet-400 animate-spin" style={{ animationDuration: "2s" }} />
                      Rewriting in your voice…
                    </div>
                  </div>
                )}
              </div>

              {/* Step indicator */}
              <div className="flex items-center justify-center gap-2 pt-1">
                {STEPS.map((s, i) => (
                  <div
                    key={s.label}
                    className="flex items-center gap-1.5 text-[11px] transition-all duration-300"
                    style={{ opacity: i === step ? 1 : 0.35 }}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full transition-all duration-300"
                      style={{
                        background: i === step
                          ? (isFinal ? "hsl(265 75% 62%)" : "hsl(188 90% 42%)")
                          : "hsl(215 20% 35%)",
                        boxShadow: i === step ? `0 0 6px ${isFinal ? "hsl(265 75% 62%)" : "hsl(188 90% 42%)"}` : "none",
                      }}
                    />
                    <span style={{ color: i === step ? (isFinal ? "hsl(265 75% 75%)" : "hsl(188 90% 70%)") : "hsl(215 20% 50%)" }}>
                      {s.label}
                    </span>
                    {i < STEPS.length - 1 && <span className="text-slate-600 ml-0.5">→</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
